import React from "react";
import { useI18n } from "@/i18n";
import { GRIND_OPTIONS } from "@/components/RecipeParamsForm";
import { Coffee, Droplets, Scale, Thermometer, Timer, CircleDot, Gauge, Snowflake } from "lucide-react";

const grindLabel = (val, lang) => {
  const o = GRIND_OPTIONS.find((g) => g.value === val);
  if (!o) return val;
  return lang === "ar" ? o.ar : o.en;
};

const Pill = ({ icon: Icon, label, value, testId }) => (
  <div className="flex items-center gap-2 rounded-lg bg-[#F9F6F0] border border-[#2A2421]/10 px-3 py-2" data-testid={testId}>
    <Icon className="w-4 h-4 text-[#C86240]" />
    <div className="leading-tight">
      <div className="text-[9px] uppercase tracking-[0.18em] text-[#5C544F]">{label}</div>
      <div className="text-sm font-semibold text-[#2A2421]">{value}</div>
    </div>
  </div>
);

export default function RecipeParamsDisplay({ params }) {
  const { t, lang, isRTL } = useI18n();
  if (!params) return null;

  const pills = [];
  if (params.dose_g != null && params.dose_g !== "")
    pills.push({ icon: Coffee, label: t.addRecipe.dose, value: `${params.dose_g} g`, k: "dose" });
  if (params.yield_g != null && params.yield_g !== "")
    pills.push({ icon: Droplets, label: t.addRecipe.yield_, value: `${params.yield_g} g`, k: "yield" });
  if (params.ratio != null && params.ratio !== "")
    pills.push({ icon: Scale, label: t.addRecipe.ratio, value: `1:${params.ratio}`, k: "ratio" });
  if (params.temp_c != null && params.temp_c !== "")
    pills.push({ icon: Thermometer, label: t.addRecipe.temp, value: `${params.temp_c}°C`, k: "temp" });
  if (params.time)
    pills.push({ icon: Timer, label: t.addRecipe.time, value: params.time, k: "time" });
  if (params.grind)
    pills.push({ icon: CircleDot, label: t.addRecipe.grind, value: grindLabel(params.grind, lang), k: "grind" });
  if (params.pressure_bar != null && params.pressure_bar !== "")
    pills.push({ icon: Gauge, label: t.addRecipe.pressure, value: `${params.pressure_bar} bar`, k: "pressure" });
  if (params.ice_g != null && params.ice_g !== "")
    pills.push({ icon: Snowflake, label: isRTL ? "الثلج" : "Ice", value: `${params.ice_g} g`, k: "ice" });

  const hasPours = Array.isArray(params.pours) && params.pours.length > 0;
  if (pills.length === 0 && !params.notes && !hasPours) return null;

  return (
    <div className="mt-1" data-testid="recipe-params-display">
      <div className="text-xs uppercase tracking-[0.25em] text-[#4A5D4E] mb-3">{t.addRecipe.paramsTitle}</div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {pills.map((p) => (
          <Pill key={p.k} icon={p.icon} label={p.label} value={p.value} testId={`param-pill-${p.k}`} />
        ))}
      </div>
      {(params.bloom_g != null && params.bloom_g !== "") || (params.bloom_s != null && params.bloom_s !== "") ? (
        <div className="mt-2 text-xs text-[#5C544F]" data-testid="param-bloom">
          {isRTL ? "Bloom: " : "Bloom: "}
          {params.bloom_g ? `${params.bloom_g} g` : ""}
          {params.bloom_g && params.bloom_s ? " · " : ""}
          {params.bloom_s ? `${params.bloom_s}s` : ""}
        </div>
      ) : null}
      {hasPours && (
        <div className="mt-4" data-testid="param-pours">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#4A5D4E] mb-2">
            {isRTL ? "تسلسل الصبّات" : "Pour sequence"}
          </div>
          <ol className="space-y-1.5">
            {params.pours.map((p, idx) => (
              <li
                key={idx}
                className="flex items-center justify-between gap-3 rounded-md bg-[#F9F6F0] border border-[#2A2421]/10 px-3 py-1.5 text-xs"
                data-testid={`param-pour-${idx}`}
              >
                <span className="text-[#5C544F]">{(isRTL ? "صبّة " : "Pour ")}{idx + 1}</span>
                <span className="font-semibold text-[#2A2421]">
                  {p.ml ? `${p.ml} ${isRTL ? "مل" : "ml"}` : "—"}
                </span>
                <span className="text-[#5C544F]">{p.at || "—"}</span>
              </li>
            ))}
          </ol>
        </div>
      )}
      {params.notes && (
        <div className="mt-3 text-xs text-[#5C544F] italic leading-relaxed" data-testid="param-notes">
          {params.notes}
        </div>
      )}
    </div>
  );
}
